import styled from 'styled-components'
import { getGetProductsByBrandIdQueryKey, useGetProductsByBrandIdV2 } from '@/apis/rest/generated/hooks'
import { ProductSortByParamParameter } from '@/apis/rest/generated/types'
import { ScrollContainer } from '@/components/domains/etc'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import { PRODUCT_CARD_SIZES } from '@/components/domains/products/new-product-card/new-product-card.type'
import SkeletonProduct from '@/components/domains/products/skeleton/SkeletonProduct'
import { MinCountForScrollContainerEnum } from '@/constants/min-count-for-scroll-container.enum'
import { PAGE_LIMIT } from '@/constants/pages.const'
import { useProductDetailEventTracker } from '@/containers/event-tracker/product-detail.event-tracker-new'
import { AdPetType } from '@/graphql/generated/schemas'
import { transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'
import { removeScrollbar } from '@/utils/utilCSS'
import type { ProductsListItemResponseDto } from '@/apis/rest/generated/types'

interface ProductCardListProps {
  /** brand ID */
  brandId: number
  /** 브랜드명 */
  brandName: string
  /** 반려동물 타입 */
  petType: AdPetType
  /** 제품 ID */
  productId: number
  /** 제품 이름 */
  productName: string
  /** 품절 여부 */
  isRealSoldOut: boolean
  /** 가격 */
  price: number
  /** 할인율 */
  discountRate: number
  /** 할인 가격 */
  salePrice: number
  /** 리뷰 점수 */
  reviewScore: number
  /** 리뷰 수 */
  reviewCount: number
}

export const ProductCardList = ({
  brandId,
  brandName,
  petType,
  productId,
  productName,
  isRealSoldOut,
  price,
  discountRate,
  salePrice,
  reviewScore,
  reviewCount,
}: ProductCardListProps) => {
  const { trackClickProductDetailMiddleProduct } = useProductDetailEventTracker()

  const { data, isLoading } = useGetProductsByBrandIdV2(
    brandId,
    {
      size: 20,
      page: PAGE_LIMIT.DEFAULT_PAGE,
      includeSoldOut: false,
      sortBy: ProductSortByParamParameter.SALES_SCORE,
      petType,
    },
    {
      query: {
        queryKey: [getGetProductsByBrandIdQueryKey(brandId, { petType }), brandId],
      },
    }
  )

  const handleClickProductCard = (product: ProductsListItemResponseDto) => {
    trackClickProductDetailMiddleProduct({
      productId: productId,
      productName: productName,
      isRealSoldOut: isRealSoldOut,
      price: price,
      discountRate: discountRate || 0,
      salePrice: salePrice,
      reviewScore: reviewScore,
      reviewCount: reviewCount,
      listType: 'samebrandproduct',
      listSection: 'samebrand',
      sectionName: `${product.brand.name}의 인기상품`,
      listIndex: flatData.findIndex((item) => item.id === product.id),
      clickedProduct: {
        brandId,
        brandName,
        productId: product.id,
        productName: product.name,
        isRealSoldOut: product.isRealSoldOut,
        price: product.price,
        discountRate: product.discountRate || 0,
        salePrice: product.salePrice,
        reviewScore: product.review.score,
        reviewCount: product.review.count,
      },
    })
  }

  const flatData = data?.data?.flatMap((item) => item) || []

  if (isLoading) {
    return (
      <StyledSkeletonProductDiv>
        <SkeletonProduct />
      </StyledSkeletonProductDiv>
    )
  }

  if (flatData?.length === 0) {
    return null
  }

  return (
    <ScrollContainer hideRightButton={flatData?.length < MinCountForScrollContainerEnum.ProductDetailBrandProduct}>
      <StyledListDiv>
        {flatData.map((product, index) => {
          if (productId === product.id) {
            return null
          }

          return (
            <NewProductCard
              key={product.id}
              product={transformProductTypeDefinedByFrontend(product)}
              size={PRODUCT_CARD_SIZES.lg}
              rankingNumber={index}
              onClickProductCard={() => handleClickProductCard(product)}
            />
          )
        })}
      </StyledListDiv>
    </ScrollContainer>
  )
}

const StyledListDiv = styled.div`
  ${removeScrollbar};
  display: flex;
  gap: 0.8rem;

  & > *:first-child {
    margin-left: 16px;
  }

  & > *:last-child {
    padding-right: 16px;
  }

  & > *:last-child {
    padding-right: 16px;
  }
`

const StyledSkeletonProductDiv = styled.div`
  display: flex;
  gap: 0.8rem;
  overflow: hidden;
  padding: 0 1.6rem;
`
